import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import { Field, reduxForm, SubmissionError } from 'redux-form';
import MorePropTypes from 'airbnb-prop-types';
import { uiState } from 'react-redux-ui-state';

import ORTypography from 'components/ORTypography/ORTypography';
import WrappedTextField from 'components/WrappedTextField/WrappedTextField';
import RBACSubmitButton from 'components/RBACWrapper/RBACSubmitButton/RBACSubmitButton';
import { OWNER, ADMIN, OR_ADMIN, OR_SUPERUSER } from 'constants/roles';
import { required, maxLength } from '../../services/validationMsgs';

const propTypes = {
  classes: PropTypes.shape({}).isRequired,
  addInstanceDatabase: PropTypes.func.isRequired,
  instanceId: PropTypes.string.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  valid: PropTypes.bool.isRequired,
  error: MorePropTypes.or([MorePropTypes.explicitNull, PropTypes.string, PropTypes.shape({})]),
  closePopout: MorePropTypes.or([MorePropTypes.explicitNull, PropTypes.func]),
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  submitSucceeded: PropTypes.bool.isRequired,
  databaseName: PropTypes.string.isRequired,
  setUiState: PropTypes.func.isRequired,
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  service: PropTypes.string.isRequired,
  pullInstanceDatabases: PropTypes.func.isRequired,
};

const defaultProps = {
  error: null,
  closePopout: null,
};

const maxLength128 = maxLength(128);

export function UnstyledAddDatabaseForm({
  classes,
  addInstanceDatabase,
  instanceId,
  handleSubmit,
  valid,
  error,
  closePopout,
  pristine,
  submitting,
  submitSucceeded,
  databaseName,
  setUiState,
  userRoles,
  service,
  pullInstanceDatabases,
}) {
  const handleFormSubmissionReturn = (result) => {
    if (result.error && result.formErrors) {
      throw new SubmissionError(result.formErrors);
    } else if (result.error && result.payload) {
      throw new SubmissionError(result.payload);
    } else {
      pullInstanceDatabases(instanceId, service);
    }
  };

  const changeDatabaseName = (event, name) => {
    setUiState({
      databaseName: name,
    });
  };

  const submitHandler = (data) =>
    addInstanceDatabase(instanceId, service, data).then(handleFormSubmissionReturn);

  const errorElm = error && (
    <ORTypography color="error" className={classes.formStatus}>
      {error}
    </ORTypography>
  );

  if (submitSucceeded && closePopout) {
    closePopout();
  }

  return (
    <form onSubmit={handleSubmit(submitHandler)}>
      <div className={classes.fieldContainer}>
        <Field
          name="name"
          label="Database Name"
          variant="outlined"
          placeholder="staging-db"
          component={WrappedTextField}
          props={{ InputLabelProps: { shrink: true, className: classes.inputLabel } }}
          validate={[maxLength128, required]}
          fullWidth
          onChange={changeDatabaseName}
        />
      </div>
      <div className={classes.actionContainer}>
        {errorElm}
        <div className={classes.addButtonContainer}>
          <RBACSubmitButton
            userAccess={userRoles}
            allowedAccess={[OWNER, ADMIN, OR_ADMIN, OR_SUPERUSER]}
            submitting={submitting}
            disabled={!valid || pristine || !databaseName}
          />
        </div>
      </div>
    </form>
  );
}

UnstyledAddDatabaseForm.propTypes = propTypes;
UnstyledAddDatabaseForm.defaultProps = defaultProps;

const styles = (theme) => ({
  fieldContainer: {
    margin: '1rem',
    '& input': {
      textAlign: 'left',
    },
    minWidth: '355px',
  },
  addButtonContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  addButton: {
    margin: theme.spacing(2),
  },
  formStatus: {
    margin: `${theme.spacing(2)}px auto auto auto`,
    fontSize: '.8rem',
    fontStyle: 'italic, Roboto Regular',
    maxWidth: '300px',
  },
  actionContainer: {
    textAlign: 'center',
  },
  inputLabel: {
    color: theme.palette.text.primary,
  },
});

export const uiConfig = {
  name: 'instanceDatabaseFormItem',
  state: () => ({
    databaseName: '',
  }),
};

export default uiState(uiConfig)(
  withStyles(styles)(reduxForm({ form: 'newDatabase' })(UnstyledAddDatabaseForm)),
);
